import {Component, OnInit, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs/Subscription';
import {Auth} from './providers/auth';
import {Api} from './providers/api';
import {EntitlementService} from './providers/entitlement.service';
import {SubscriptionExpiredModalComponent} from './shared/subscription-expired-modal/subscription-expired-modal.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'app';
  trialBanner: string = null;
  trialDaysRemaining: number = null;
  subscriptionExpiresAt: string | null = null;
  daysUntilExpiry: number | null = null;
  billingCycle: string | null = null;
  paymentProvider: string | null = null;
  accountStatus: string = null;
  private pollInterval: any = null;
  private retryTimeout: any = null;
  private currentUrl: string = '';
  private expiredModalRef: NgbModalRef | null = null;
  private subscriptionChangedSub: Subscription | null = null;

  constructor(private _router: Router, private auth: Auth, private api: Api, private modalService: NgbModal, private entitlement: EntitlementService) {}

  ngOnInit() {
    this._router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
    this.currentUrl = this._router.url || '';
    this._router.events.subscribe(function(evt) {
      if (evt instanceof NavigationEnd) {
        this._router.navigated = false;
        window.scrollTo(0, 0);
        this.currentUrl = evt.urlAfterRedirects || evt.url || this._router.url || '';
        this.checkTrialStatus();
      }
    }.bind(this));
    if (this.auth && (this.auth as any).subscriptionChanged) {
      this.subscriptionChangedSub = (this.auth as any).subscriptionChanged.subscribe(function() {
        this.checkTrialStatus();
      }.bind(this));
    }
    this.startTrialPolling();
  }

  ngOnDestroy() {
    if (this.pollInterval) { clearInterval(this.pollInterval); }
    if (this.retryTimeout) { clearTimeout(this.retryTimeout); }
    if (this.subscriptionChangedSub) { this.subscriptionChangedSub.unsubscribe(); }
    this.closeExpiredModal();
  }

  isPublicRoute(url: string): boolean {
    if (!url) { return true; }
    let u = url;
    const qIdx = u.indexOf('?');
    if (qIdx !== -1) { u = u.substring(0, qIdx); }
    const hashIdx = u.indexOf('#');
    if (hashIdx !== -1) { u = u.substring(hashIdx + 1); }
    while (u.length > 0 && u.charAt(0) === '/') { u = u.substring(1); }
    if (u === '' || u === '/') { return true; }
    if (u.indexOf('login') === 0) { return true; }
    if (u.indexOf('registro') === 0) { return true; }
    if (u.indexOf('register-wizard') === 0) { return true; }
    if (u.indexOf('wizard') === 0) { return true; }
    if (u.indexOf('signup') === 0) { return true; }
    if (u.indexOf('activacion') === 0) { return true; }
    if (u.indexOf('activate') === 0) { return true; }
    if (u.indexOf('recuperar-codigo') === 0) { return true; }
    if (u.indexOf('recuperar') === 0) { return true; }
    if (u.indexOf('recoveryPass') === 0) { return true; }
    if (u.indexOf('pago') === 0) { return true; }
    if (u.indexOf('system/maintainer') === 0) { return true; }
    if (u.indexOf('system') === 0) { return true; }
    if (u.indexOf('maintainer') === 0) { return true; }
    return false;
  }

  get shouldShowBanner(): boolean {
    return !!this.trialBanner && !!this.auth && !!this.auth.user && !this.isPublicRoute(this.currentUrl);
  }

  get shouldShowExpiredModal(): boolean {
    return this.accountStatus === 'EXPIRED' && !!this.auth && !!this.auth.user && !this.isPublicRoute(this.currentUrl);
  }

  private openExpiredModal(): void {
    if (this.expiredModalRef) { return; }
    if (this.isPublicRoute(this.currentUrl)) { return; }
    try {
      this.expiredModalRef = this.modalService.open(SubscriptionExpiredModalComponent, {backdrop: 'static', keyboard: false});
      this.expiredModalRef.componentInstance.expiresAt = this.subscriptionExpiresAt;
      this.expiredModalRef.componentInstance.daysUntilExpiry = this.daysUntilExpiry;
      var self = this;
      this.expiredModalRef.result.then(function() { self.expiredModalRef = null; }, function() { self.expiredModalRef = null; });
    } catch (e) {
      this.expiredModalRef = null;
    }
  }

  private closeExpiredModal(): void {
    if (this.expiredModalRef) {
      try { this.expiredModalRef.close(); } catch (e) { try { this.expiredModalRef.dismiss(); } catch(e2) {} }
      this.expiredModalRef = null;
    }
  }

  private checkTrialStatus(): void {
    if (this.auth && this.auth.user && this.auth.user.circuito && this.auth.user.circuito[0] && this.auth.user.circuito[0].guid) {
      var guid = this.auth.user.circuito[0].guid;
      this.api.getTrialStatus(guid).subscribe(function(res: any) {
        var data = res && res.data ? res.data : res;
        this.entitlement.setFromStatus(data);
        this.accountStatus = data.accountStatus || data.subscriptionStatus || null;
        if (this.accountStatus) { this.accountStatus = String(this.accountStatus).toUpperCase(); }
        if (data.trialEndsAt) {
          var diff = new Date(data.trialEndsAt).getTime() - Date.now();
          this.trialDaysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        } else {
          this.trialDaysRemaining = null;
        }
        this.subscriptionExpiresAt = data.subscriptionExpiresAt || null;
        this.billingCycle = data.billingCycle || null;
        this.paymentProvider = data.paymentProvider || null;
        if (data.daysUntilExpiry !== undefined && data.daysUntilExpiry !== null) {
          this.daysUntilExpiry = Number(data.daysUntilExpiry);
        } else if (this.subscriptionExpiresAt) {
          var diff2 = new Date(this.subscriptionExpiresAt).getTime() - Date.now();
          this.daysUntilExpiry = Math.max(0, Math.ceil(diff2 / (1000 * 60 * 60 * 24)));
        } else {
          this.daysUntilExpiry = null;
        }
        // LIFETIME: both dates null, no banner, no modal
        if (this.accountStatus === 'LIFETIME') {
          this.trialBanner = null;
          this.closeExpiredModal();
          return;
        }
        if (this.accountStatus === 'EXPIRED') {
          this.trialBanner = null;
          if (!this.isPublicRoute(this.currentUrl)) {
            this.openExpiredModal();
          } else {
            this.closeExpiredModal();
          }
          return;
        }
        this.closeExpiredModal();
        if (this.accountStatus === 'ACTIVE' && this.subscriptionExpiresAt) {
          if (this.daysUntilExpiry !== null && this.daysUntilExpiry <= 7) {
            this.trialBanner = 'Vence en ' + this.daysUntilExpiry + ' días — Renovar';
          } else {
            this.trialBanner = null; // hide after paying, only show when <=7 days
          }
        } else if (this.accountStatus === 'TRIAL' && this.trialDaysRemaining !== null) {
          this.trialBanner = this.trialDaysRemaining + ' días restantes de prueba';
        } else if (this.accountStatus === 'ACTIVE' && !this.subscriptionExpiresAt) {
          // ACTIVE without expiry — treat as active but no date, show generic
          this.trialBanner = null;
        } else {
          this.trialBanner = null;
        }
      }.bind(this), function() {
        // ignore poll errors
      }.bind(this));
    } else {
      this.trialBanner = null;
      this.accountStatus = null;
      this.trialDaysRemaining = null;
      this.subscriptionExpiresAt = null;
      this.daysUntilExpiry = null;
      this.billingCycle = null;
      this.paymentProvider = null;
      this.entitlement.reset();
      this.closeExpiredModal();
    }
  }

  private startTrialPolling() {
    this.checkTrialStatus();
    this.pollInterval = setInterval(function() { this.checkTrialStatus(); }.bind(this), 60000);
    if (!this.auth || !this.auth.user) {
      this.retryTimeout = setTimeout(function() { this.checkTrialStatus(); }.bind(this), 1500);
    }
  }

  get isUploadBlocked(): boolean {
    return this.accountStatus === 'EXPIRED';
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts